import { useCallback, useState } from "react"
import { toast } from "sonner"

/**
 * Rails' `errors.as_json` shape: `{ name: ["can't be blank"] }`.
 *
 * Tasks and files use a *different* server shape — `inertia_errors` flattens
 * to `{ title: "Title can't be blank" }` — so both are accepted here and
 * normalised, rather than having each form guess which one it will get.
 */
export type FieldErrors = Record<string, string[]>

function normaliseErrors(raw: unknown): FieldErrors {
  if (!raw || typeof raw !== "object") return {}
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).map(([field, messages]) => [
      field,
      Array.isArray(messages) ? messages.map(String) : [String(messages)],
    ]),
  )
}

interface Options<T> {
  /** POST target. */
  path: string
  /** Rails wraps params under the model name: { company: {...} }. */
  param: string
  initial: T
  onSuccess?: (record: { uid: string; name?: string }) => void
  /** Key the controller returns the record under; defaults to `param`. */
  resultKey?: string
  successMessage?: string
  /**
   * Last chance to reshape state before it goes to the server, for the cases
   * where the control and the column disagree — the company combobox is a
   * multi-select in single-select mode, so it holds an array where the
   * server wants one id.
   */
  transform?: (data: T) => Record<string, unknown>
}

/**
 * One submit path for every record create form.
 *
 * The four record forms each carried their own copy of this — the same CSRF
 * header lookup, the same fetch, the same `if (data.fields) setErrors(...)`,
 * the same footer. Four copies meant four places for the same two bugs:
 *
 *   - A non-ok response with no `fields` (403, 409, a Rails 500 page) fell
 *     through the `if` and returned silently. The form just stopped, with no
 *     message and the button re-enabled, and looked like the click missed.
 *   - `await response.json()` was called before checking the content type, so
 *     any HTML error page threw a SyntaxError and surfaced as the generic
 *     "Failed to create X" — hiding what actually went wrong.
 *
 * Both are handled once, here.
 */
export function useRecordForm<T extends Record<string, unknown>>({
  path,
  param,
  initial,
  onSuccess,
  resultKey,
  successMessage,
  transform,
}: Options<T>) {
  const [data, setData] = useState<T>(initial)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const setField = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setData((prev) => ({ ...prev, [field]: value }))
    // Clearing on edit means the message goes away when you fix the thing it
    // is about, rather than lingering until the next submit.
    setErrors((prev) => {
      if (!prev[field as string]) return prev
      const next = { ...prev }
      delete next[field as string]
      return next
    })
  }, [])

  const submit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault()
      setErrors({})
      setIsSubmitting(true)

      try {
        const response = await fetch(path, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") || "",
            Accept: "application/json",
          },
          body: JSON.stringify({ [param]: transform ? transform(data) : data }),
        })

        // Check the content type before parsing: a Rails error page is HTML,
        // and calling .json() on it throws something unrelated to the problem.
        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json")
        const body = isJson ? await response.json() : null

        if (!response.ok) {
          const fields = normaliseErrors(body?.fields)
          setErrors(fields)
          // Never fail silently. If the server did not name a field, say
          // whatever it did say, and fall back to the status.
          if (Object.keys(fields).length === 0) {
            toast.error(
              body?.errors?.[0] ??
                body?.error ??
                `Could not save (${response.status}).`,
            )
          }
          return false
        }

        if (successMessage ?? body?.message) {
          toast.success(successMessage ?? body.message)
        }
        setData(initial)
        onSuccess?.(body?.[resultKey ?? param])
        return true
      } catch (error) {
        console.error(error)
        toast.error("Could not reach the server. Check your connection.")
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [path, param, data, initial, onSuccess, resultKey, successMessage, transform],
  )

  return { data, setField, setData, errors, setErrors, isSubmitting, submit }
}
